import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { FiEye, FiEyeOff, FiLock } from "react-icons/fi";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { resetPassword } from '../api/auth'; // Adjust the import path as needed
import { useNavigate } from "react-router-dom";
import favicon from '../favicon.ico';

const SigninSchema = Yup.object().shape({
    emailOrUsername: Yup.string().required("Email or Username is required"),
    password: Yup.string()
        .min(6, "Password too short")
        .required("Password is required"),
});

const ResetPassword = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-200">
            <div className="w-full max-w-md p-6 bg-blue-600 text-white shadow-lg rounded-lg">
                <div className="text-center mb-6">
                    <img 
                        src={favicon}
                        alt="Logo" 
                        className="mx-auto mb-4 w-16"
                    />
                    <h2 className="text-2xl font-bold text-gray-800">Reset Password</h2>
                    <p className="text-gray-600">Create Your New Password</p>
                </div>

                <Formik
                    initialValues={{
                        password: "",
                        confirmPassword: "",
                    }}
                    validationSchema={SigninSchema}
                    onSubmit={async (values, { setSubmitting, resetForm }) => {
                        try {
                            const response = await resetPassword(values);
                            const username = response.username;
                            if(response.status === "success"){
                                navigate(`/profile/${encodeURIComponent(username)}`);
                                resetForm();
                            }
                            //console.log("Sign-In Response:", response);
                        } catch (error) {
                            //console.error("Sign-In Error:", error);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            {/* Password Input */}
                            <div className="relative mb-4">
                                <Field
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    placeholder="Password"
                                    className="w-full p-3 pl-10 bg-white text-gray-900 border border-gray-300 rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <FiLock className="absolute left-3 top-3 text-gray-400" />
                                <div
                                    className="absolute right-3 top-3 cursor-pointer text-gray-400"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                >
                                    {showPassword ? <FiEyeOff /> : <FiEye />}
                                </div>
                                <ErrorMessage
                                    name="password"
                                    component="div"
                                    className="text-sm text-red-500 mt-1"
                                />
                            </div>

                            {/* Confirm Password Input */}
                            <div className="relative mb-4">
                                <Field
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    placeholder="Confirm Password"
                                    className="w-full p-3 pl-10 bg-white text-gray-900 border border-gray-300 rounded-lg placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <FiLock className="absolute left-3 top-3 text-gray-400" />
                                <div
                                    className="absolute right-3 top-3 cursor-pointer text-gray-400"
                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                >
                                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                                </div>
                                <ErrorMessage
                                    name="confirmPassword"
                                    component="div"
                                    className="text-sm text-red-500 mt-1"
                                />
                            </div>

                            {/* Terms & Conditions */}
                            <div className="flex items-center mb-4">
                                <Field
                                    type="checkbox"
                                    name="terms"
                                    className="w-4 h-4 text-blue-600 bg-gray-200 rounded focus:ring-2 focus:ring-purple-500"
                                />
                                <label className="ml-2 text-sm text-white">
                                    I Agree to{" "}
                                    <span className="font-semibold underline">
                                        <Link
                                                to="/terms_and_conditions"
                                                className="font-semibold underline hover:text-gray-300"
                                                >                
                                                Terms & Conditions
                                        </Link>
                                    </span>
                                </label>
                                <ErrorMessage
                                    name="terms"
                                    component="div"
                                    className="text-sm text-red-500 ml-2"
                                />
                            </div>

                            {/* Sign-In Button */}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full py-3 rounded-lg font-semibold transition duration-300 ${
                                    isSubmitting
                                        ? "bg-blue-700 cursor-not-allowed"
                                        : "bg-blue-400 hover:bg-blue-700"
                                }`}
                            >
                                {isSubmitting ? "Resetting Password..." : "Reset Password"}
                            </button>
                            
                            {/* Footer Link */}
                            <p className="text-center text-sm text-white">
                                Don't have an account?{" "}
                                <Link
                                    to="/sign_up"
                                    className="font-semibold underline hover:text-gray-300"
                                >
                                    Sign Up Here
                                </Link>
                            </p>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default ResetPassword;
